/**
 * Pure mutation functions for element-related actions.
 */

import type {AddElementsParam} from "@buildwithflux/core";
import {
    ensureRouteVertexState,
    getAllBomExportableDesignatorsFrom,
    handleAddElements,
    PcbNodeHelper,
    PcbTreeManager,
    removeNodesAndDescendants,
    setElementsAndGenerateNodes,
} from "@buildwithflux/core";
import type {
    AnyPcbNode,
    IDocumentData,
    IElementData,
    IPartVersionData,
    IPropertiesMap,
    PcbNodesMap,
    VertexUpdate,
} from "@buildwithflux/models";
import {getSubTreeUids} from "@buildwithflux/models";
import {applyChange} from "deep-diff";
import {pickBy} from "lodash";

import {updateElementToUsePartVersion} from "modules/document/state/actionCreators/documentHelpers";
import {FluxLogger} from "modules/storage_engine/connectors/LogConnector";
import {PartStorageHelper} from "modules/storage_engine/helpers/PartStorageHelper";

/**
 * Pure mutation: adds elements to the document draft.
 * Used by both the action handler and the direct store method.
 */
export function applyAddElements(draft: IDocumentData, payload: AddElementsParam & {inPcbEditor: boolean}): void {
    handleAddElements(draft, payload);
    draft.updated_at = new Date().getTime();
}

/**
 * Pure mutation: updates element schematic positions and applies vertex updates.
 * Vertex updates are applied FIRST (before element positions) to match Redux behavior.
 */
export function applyUpdateElementsSchematicPosition(
    draft: IDocumentData,
    elements: IElementData[],
    vertexUpdates?: VertexUpdate[],
): void {
    const hasVertexUpdates = vertexUpdates && vertexUpdates.length > 0;
    if (hasVertexUpdates) {
        for (const update of vertexUpdates) {
            applyChange(draft.routeVertices, true, update);
        }
        ensureRouteVertexState(draft);

        const pcbTreeManager = new PcbTreeManager(draft.pcbLayoutNodes, FluxLogger);
        pcbTreeManager.updateNetNodes({...draft});
        draft.pcbLayoutNodes = pcbTreeManager.treeNodes as PcbNodesMap<AnyPcbNode>;
    }

    for (const updatedElement of elements ?? []) {
        const element = draft.elements[updatedElement.uid];
        if (!element) {
            continue;
        }

        element.diagram_position = Object.values(updatedElement.symbol_placements ?? {})[0];
        element.symbol_placements = updatedElement.symbol_placements;
        element.updated_at = new Date().getTime();
    }
}

/**
 * Pure mutation: updates element properties.
 */
export function applyUpdateElementProperties(
    draft: IDocumentData,
    elementUid: string,
    properties: IPropertiesMap,
): void {
    const draftElement = draft.elements[elementUid];
    if (draftElement) {
        draftElement.properties = properties;
    }
}

/**
 * Pure mutation: flips elements and applies route vertex updates.
 * Route vertex updates are applied FIRST (before element positions) to match Redux behavior.
 */
export function applyFlipElements(
    draft: IDocumentData,
    elements: IElementData[],
    routeVertexUpdates?: VertexUpdate[],
): void {
    for (const updatedVertices of routeVertexUpdates ?? []) {
        applyChange(draft.routeVertices, true, updatedVertices);
    }

    for (const updatedElement of elements) {
        const oldElement = draft.elements[updatedElement.uid];
        if (!oldElement) {
            continue;
        }
        oldElement.diagram_position = Object.values(updatedElement.symbol_placements ?? {})[0];
        oldElement.symbol_placements = updatedElement.symbol_placements;
        oldElement.updated_at = new Date().getTime();
    }
}

/**
 * Pure mutation: updates part version data cache for elements matching the given partUid.
 */
export function applySetPartVersionDataCache(
    draft: IDocumentData,
    elementsToChange: IElementData[],
    partVersionData: IPartVersionData,
): void {
    for (const elementData of elementsToChange) {
        const element = draft.elements[elementData.uid];
        if (!element) {
            continue;
        }

        PartStorageHelper.inheritPartProperties(partVersionData, element);

        const nodeUidsToRemove = getSubTreeUids({
            pcbNodes: draft.pcbLayoutNodes,
            nodeUid: elementData.uid,
            includeRoot: true,
        }).filter((nodeUid: string) => PcbNodeHelper.isNodeInElementSubLayout(nodeUid, elementData.uid));

        removeNodesAndDescendants(draft.pcbLayoutNodes, nodeUidsToRemove);

        const otherElementsInHostDoc = pickBy(draft.elements, (el) => el.uid !== element.uid);
        updateElementToUsePartVersion({
            element,
            partVersionData,
            otherDesignatorsAlreadyInProject: getAllBomExportableDesignatorsFrom(otherElementsInHostDoc),
        });

        setElementsAndGenerateNodes(draft, [element]);
    }
}
